import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getGameBySlug, gamesData } from '../data/games';
import { Badge, Button } from '../components/ui';
import { PlatformIcon, ArrowLeft, ArrowRight, PlayIcon, StarIcon } from '../components/common/Icons';

const getStatusVariant = (status) => {
  const variants = {
    'Available Now': 'available',
    'Coming Soon': 'coming-soon',
    'Beta': 'beta',
    'Early Access': 'early-access',
    'Announced': 'announced',
    'In Development': 'in-development',
  };
  return variants[status] || 'default';
};

const GameDetailPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const game = getGameBySlug(slug);

  if (!game) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="text-6xl mb-4">🎮</div>
          <h1 className="text-3xl font-bold mb-4">Game Not Found</h1>
          <p className="text-gray-400 mb-8">
            The game you're looking for doesn't exist.
          </p>
          <Link to="/games" className="btn-primary">
            Browse All Games
          </Link>
        </div>
      </div>
    );
  }

  // Get related games (same genre)
  const relatedGames = gamesData
    .filter(
      (g) =>
        g.id !== game.id &&
        g.genres.some((genre) => game.genres.includes(genre))
    )
    .slice(0, 3);

  return (
    <div className="min-h-screen pt-24 pb-16">
      {/* Hero Section */}
      <div
        className="relative h-[50vh] md:h-[60vh] flex items-end"
        style={{ background: game.image }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 pb-12 w-full">
          {/* Back Button */}
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-6"
          >
            <ArrowLeft className="w-5 h-5" />
            Back
          </button>

          <div className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Badge variant={getStatusVariant(game.status)} size="lg" className="mb-4">
                {game.status}
              </Badge>
              <h1 className="font-gaming text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
                {game.title}
              </h1>
              <p className="text-xl text-gray-300 max-w-2xl">{game.tagline}</p>
            </div>

            <div className="flex gap-4">
              {game.status === 'Available Now' && (
                <Button variant="primary" size="lg" icon={<PlayIcon />}>
                  PLAY NOW
                </Button>
              )}
              {game.trailer && (
                <Button variant="secondary" size="lg">
                  WATCH TRAILER
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-12">
            {/* Description */}
            <section>
              <h2 className="text-2xl font-bold mb-4">About This Game</h2>
              <p className="text-gray-400 leading-relaxed">{game.description}</p>
              {game.longDescription && (
                <div className="mt-6 text-gray-400 leading-relaxed whitespace-pre-line">
                  {game.longDescription}
                </div>
              )}
            </section>

            {/* Genres */}
            <section>
              <h2 className="text-2xl font-bold mb-4">Genres</h2>
              <div className="flex flex-wrap gap-3">
                {game.genres.map((genre) => (
                  <Link
                    key={genre}
                    to={`/games?genre=${genre}`}
                    className="px-4 py-2 bg-white/5 rounded-xl hover:bg-white/10 transition-colors"
                  >
                    {genre}
                  </Link>
                ))}
              </div>
            </section>

            {/* Related Games */}
            {relatedGames.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold mb-6">You Might Also Like</h2>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
                  {relatedGames.map((relatedGame) => (
                    <Link
                      key={relatedGame.id}
                      to={`/games/${relatedGame.slug}`}
                      className="card-hover group rounded-2xl overflow-hidden bg-white/5 border border-white/10"
                    >
                      <div
                        className="aspect-video relative"
                        style={{ background: relatedGame.image }}
                      >
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/10 transition-colors" />
                      </div>
                      <div className="p-4">
                        <h3 className="font-bold group-hover:text-primary-500 transition-colors">
                          {relatedGame.title}
                        </h3>
                        <p className="text-gray-500 text-sm mt-1">
                          {relatedGame.tagline}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </section>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Game Info Card */}
            <div className="bg-white/5 rounded-3xl border border-white/10 p-6 space-y-6">
              <h3 className="text-xl font-bold">Game Info</h3>

              {game.rating && (
                <div>
                  <span className="text-gray-500 text-sm block mb-1">Rating</span>
                  <div className="flex items-center gap-2">
                    <div className="flex text-yellow-400">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <StarIcon
                          key={star}
                          className="w-5 h-5"
                          filled={star <= Math.floor(game.rating)}
                        />
                      ))}
                    </div>
                    <span className="font-bold">{game.rating}</span>
                  </div>
                </div>
              )}

              <div>
                <span className="text-gray-500 text-sm block mb-1">Developer</span>
                <span className="font-medium">{game.developer}</span>
              </div>

              <div>
                <span className="text-gray-500 text-sm block mb-1">Publisher</span>
                <span className="font-medium">{game.publisher}</span>
              </div>

              <div>
                <span className="text-gray-500 text-sm block mb-1">Release Date</span>
                <span className="font-medium">{game.releaseDate}</span>
              </div>

              <div>
                <span className="text-gray-500 text-sm block mb-1">Players</span>
                <span className="font-medium">{game.players}</span>
              </div>

              {game.downloads && (
                <div>
                  <span className="text-gray-500 text-sm block mb-1">Downloads</span>
                  <span className="font-medium text-primary-500">{game.downloads}</span>
                </div>
              )}
            </div>

            {/* Platforms Card */}
            <div className="bg-white/5 rounded-3xl border border-white/10 p-6">
              <h3 className="text-xl font-bold mb-4">Available On</h3>
              <div className="grid grid-cols-3 gap-4">
                {game.platforms.map((platform) => (
                  <div
                    key={platform}
                    className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
                  >
                    <PlatformIcon platform={platform} className="w-8 h-8 text-gray-400" />
                    <span className="text-xs text-gray-500 capitalize">{platform}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* CTA Card */}
            {game.status === 'Available Now' && (
              <div className="bg-gradient-to-br from-primary-500/20 to-secondary-400/20 rounded-3xl border border-white/10 p-6 text-center">
                <h3 className="text-xl font-bold mb-2">Ready to Play?</h3>
                <p className="text-gray-400 text-sm mb-4">
                  Start your African adventure today!
                </p>
                <Button variant="primary" className="w-full">
                  GET THE GAME
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GameDetailPage;
